import ArsIdfLogo from '@/assets/ArsIdfLogo.png';
import PromArsHeroImage from '@/assets/Onboarding/PromArsHeroImage.jpg';
import { LandingPageFooter } from '@/pages/onboarding/LandingPageFooter';
import { PatientPageLayout } from '@/components/PatientPageLayout';
import { useLandingProfessionalSelect } from '@/hooks/useLandingProfessionalSelect';

/**
 * Copie de la page d'accueil PROM ARS. Volontairement courte et concrète : le
 * patient doit comprendre en quelques secondes ce qu'on lui demande et
 * combien de temps cela va lui prendre. Pas de jargon — ni « PROM », ni
 * « KCCQ », qui n'apprennent rien au patient. « Insuffisance cardiaque »
 * reste, en revanche : c'est son diagnostic, et le questionnaire ne parle que
 * de ça.
 */
export const PROM_ARS_LANDING_COPY = {
  title: ["Mesurer l'impact", 'de votre insuffisance', 'cardiaque'],
  paragraphs: [
    "Vivre avec une insuffisance cardiaque, ce n'est pas qu'une affaire d'examens et de traitements : c'est aussi ce que cela change dans vos journées.",
    'Les questions qui suivent portent sur votre essoufflement, votre fatigue, et sur ce que vous arrivez encore à faire. Comptez environ 5 minutes.',
  ],
  buttonLabel: 'Commencer',
} as const;

/**
 * Accueil dédié au variant PROM ARS (questionnaires de résultat rapporté par le
 * patient demandés par l'ARS, remplis APRÈS la consultation).
 *
 * Page parallèle à `ProfessionalSelect` plutôt qu'un branchement dedans : le
 * patient ne prépare pas un rendez-vous et n'a pas de médecin à choisir, donc
 * ni le titre, ni le carrousel de sélection, ni le CTA de ce composant ne
 * s'appliquent. Même approche que les variants CH de Bligny et ETP sommeil,
 * qui ont chacun leur étape d'accueil (ALDO-2148).
 *
 * Le parcours est commandé par l'ARS : l'accueil est signé par le bloc
 * institutionnel ARS Île-de-France, et le médecin n'y est plus nommé
 * (ALDO-2742).
 */
export const LandingPromArs = () => {
  // La carte du médecin a disparu, mais le hook reste appelé : c'est lui qui
  // pousse le professionnel dans redux (`updateProfessional`). Sans cet appel,
  // la passation perdrait son rattachement et le compte rendu son destinataire.
  const { handleNavigateNext } = useLandingProfessionalSelect();

  return (
    <PatientPageLayout
      title={
        <div className="flex flex-col items-center gap-4">
          <img
            src={ArsIdfLogo}
            alt="République française – Agence Régionale de Santé Île-de-France"
            className="w-56"
          />
          <h1 className="text-2xl min-[23rem]:text-3.5xl font-semibold leading-tight text-indigo-900 text-center">
            {PROM_ARS_LANDING_COPY.title.map((line, index) => (
              <span key={line}>
                {index > 0 && <br />}
                {line}
              </span>
            ))}
          </h1>
        </div>
      }
      titleClassName="text-center"
      contentClassName="flex flex-col items-center justify-start text-center"
      footer={
        <div className="w-full flex flex-col items-center gap-4">
          <LandingPageFooter
            buttonLabel={PROM_ARS_LANDING_COPY.buttonLabel}
            onClick={handleNavigateNext}
          />
        </div>
      }
    >
      <div className="w-full flex flex-col items-center text-center gap-2">
        {/* Purement illustratif : `alt` vide pour que les lecteurs d'écran
            passent directement au texte, qui porte toute l'information. */}
        <img
          src={PromArsHeroImage}
          alt=""
          className="mt-2 max-h-40 w-full max-w-md rounded-2xl object-cover"
        />

        <div className="mt-4 flex max-w-md flex-col gap-y-3 px-4 text-center text-base font-light leading-relaxed text-indigo-900">
          {PROM_ARS_LANDING_COPY.paragraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>
      </div>
    </PatientPageLayout>
  );
};
